import React from 'react';
import { Box, Unstable_Grid2 as Grid, useTheme } from '@mui/material';

import { Text, TextField } from 'components';
import { useToast } from 'core/toast/hooks';
import { useHasMobileView } from 'hooks/useHasMobileView';
import { useTranslations } from 'hooks/useTranslations';

import { UseMutation, UseQuery } from 'useQuery';
import { Commission } from 'useQuery/types';

import { FrequencyField, SectionSaveFooter } from './InvoicingShared';

interface OrganisationCommissionsProps {
  id: string;
  commissions: Commission[] | null;
}

function OrganisationCommissions({ id, commissions }: OrganisationCommissionsProps) {
  const theme = useTheme();
  const { addToast } = useToast();
  const translations = useTranslations('organisationInvoicing');
  const { hasMobileView } = useHasMobileView();

  const { data: contract, refetch: refetchContract } = UseQuery.invoicingContract({ id });

  const [commissionData, setCommissionData] = React.useState<Commission[]>([]);
  const [frequencyMonths, setFrequencyMonths] = React.useState(1);

  const {
    mutateAsync: mutateAsyncUpdateOrganisationCommissions,
    isLoading: loadingUpdateOrganisationComissions,
  } = UseMutation.updateOrganisationCommissions();
  const { mutateAsync: updateContract, isLoading: loadingContract } =
    UseMutation.updateInvoicingContract();

  React.useEffect(() => {
    setCommissionData(commissions ?? []);
  }, [commissions]);

  React.useEffect(() => {
    setFrequencyMonths(contract?.commission_frequency_months ?? 1);
  }, [contract]);

  const percentagesChanged = React.useMemo(() => {
    const server = commissions ?? [];
    if (commissionData.length !== server.length) {
      return commissionData.length > 0;
    }
    return commissionData.some(
      (commission, index) => commission.commission_percent !== server[index]?.commission_percent,
    );
  }, [commissionData, commissions]);

  const frequencyChanged = frequencyMonths !== (contract?.commission_frequency_months ?? 1);
  const hasChanges = percentagesChanged || frequencyChanged;

  async function save() {
    try {
      if (percentagesChanged) {
        await mutateAsyncUpdateOrganisationCommissions({ id, commissions: commissionData });
      }
      if (frequencyChanged) {
        await updateContract({
          id,
          contract: { commission_frequency_months: frequencyMonths },
        });
        await refetchContract();
      }
      addToast({ title: translations.commissionsSaved, severity: 'success' });
    } catch (error) {
      // handled by the mutations' onError toasts
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        padding: 2,
        marginBottom: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        maxWidth: `calc(100vw - ${theme.spacing(hasMobileView ? 4 : 6)})`,
      }}
    >
      <Text.Bold>{translations.commissionsTitle}</Text.Bold>

      <Box sx={{ paddingTop: 2, maxWidth: 480 }}>
        <FrequencyField months={frequencyMonths} onChange={setFrequencyMonths} />
        <Box sx={{ paddingTop: 0.5 }}>
          <Text.Small color="gray">{translations.commissionsHint}</Text.Small>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ paddingTop: 2 }}>
        {commissionData?.map((commission, index) => (
          <Grid key={commission.vehicle_provider_id} xs={12} md={6}>
            <TextField
              type="number"
              label={`${commission.label} - %`}
              value={commission.commission_percent}
              onChange={(value) => {
                const next = [...commissionData];
                next[index] = {
                  commission_percent: Math.max(Math.min(value, 10), 0),
                  label: commission.label,
                  vehicle_provider_id: commission.vehicle_provider_id,
                };
                setCommissionData(next);
              }}
              fullWidth
            />
          </Grid>
        ))}
      </Grid>

      <SectionSaveFooter
        dirty={hasChanges}
        saving={loadingUpdateOrganisationComissions || loadingContract}
        onSave={save}
      />
    </Box>
  );
}

export { OrganisationCommissions };
